import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'
import { RecommendationCards } from '@/components/v2/tools/amazon-listing/shared/RecommendationCards'
import { SalesPsychologyCard } from '@/components/v2/tools/amazon-listing/shared/SalesPsychologyCard'

interface ImageAuditOverviewProps {
  imageAudit: ListingAuditReport['image_audit']
}

export function ImageAuditOverview({ imageAudit }: ImageAuditOverviewProps) {
  const { t } = useTranslation()
  const images = imageAudit?.images ?? []
  const r = (key: string) => t(`amazonListing.report.${key}`)

  return (
    <div className="space-y-4">
      <OverviewHeader imageAudit={imageAudit} images={images} r={r} />

      {images.map((img, i) => <DetailedImageCard key={i} img={img} r={r} />)}

      {imageAudit?.gallery_assessment && <GalleryAssessment ga={imageAudit.gallery_assessment} r={r} />}
      {imageAudit?.visual_narrative && <VisualNarrative vn={imageAudit.visual_narrative} r={r} />}
      {imageAudit?.missing_image_types?.length > 0 && <MissingTypes types={imageAudit.missing_image_types} r={r} />}

      {Array.isArray(imageAudit?.competitor_strategies) && imageAudit.competitor_strategies.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
          <h4 className="text-sm font-medium mb-2">{r('competitorStrategies')}</h4>
          <ul className="space-y-1 text-sm text-neutral-600 dark:text-neutral-400">
            {imageAudit.competitor_strategies.map((s, i) => <li key={i}>• {s}</li>)}
          </ul>
        </div>
      )}

      {imageAudit?.sales_psychology && <SalesPsychologyCard data={imageAudit.sales_psychology} />}
      {imageAudit?.recommendations?.length > 0 && <RecommendationCards recs={imageAudit.recommendations} t={t} />}
    </div>
  )
}

/* ---------- Overview with intro, avg scores, thumbnails ---------- */

function OverviewHeader({ imageAudit, images, r }: {
  imageAudit: ListingAuditReport['image_audit']; images: ListingAuditReport['image_audit']['images']; r: (k: string) => string
}) {
  const avgDims = calcAvgDimensions(images)
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-neutral-900 dark:text-white">{r('imageAudit')}</h3>
        <span className="text-lg font-bold">{imageAudit?.overall_score ?? 0}</span>
      </div>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
        <Info className="w-4 h-4 inline mr-1 -mt-0.5" />{r('imageAuditIntro')}
      </p>

      {/* Average dimension scores */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {avgDims.map(({ key, avg }) => (
          <div key={key} className="rounded-lg bg-neutral-50 dark:bg-neutral-700/50 p-3">
            <div className="text-xs text-neutral-500 mb-1">{r(key)}</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${dimBarColor(avg)}`} style={{ width: `${avg}%` }} />
              </div>
              <span className="text-sm font-bold text-neutral-900 dark:text-white">{Math.round(avg)}</span>
            </div>
            <span className={`text-2xs font-medium ${dimStatusColor(avg)}`}>{dimStatusLabel(avg, r)}</span>
          </div>
        ))}
      </div>

      {/* Thumbnail strip */}
      {images.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <div key={i} className="relative flex-shrink-0">
              {img.url ? (
                <img src={img.url} alt={`Pos ${img.position}`} className="w-16 h-16 object-cover rounded-lg border border-neutral-200 dark:border-neutral-600" loading="lazy" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center text-xs text-neutral-400">#{img.position}</div>
              )}
              <span className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full text-3xs font-bold flex items-center justify-center text-white ${scoreBadgeBg(imageScore(img.dimensions))}`}>
                {img.position}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ---------- Detailed per-image card (like competitor) ---------- */

function DetailedImageCard({ img, r }: { img: ListingAuditReport['image_audit']['images'][0]; r: (k: string) => string }) {
  const dims = img.dimensions || {}
  const pct = imageScore(dims)
  const dimEntries = [
    { key: 'designQuality', val: dims.design_quality },
    { key: 'perceivedValue', val: dims.perceived_value },
    { key: 'messageStrength', val: dims.message_strength },
    { key: 'messageClarity', val: dims.message_clarity },
  ]

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-neutral-500 bg-neutral-100 dark:bg-neutral-700 px-2 py-0.5 rounded">Pos {img.position}</span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-white">{r('detailedImageAudit')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${scoreColor(pct)}`}>{pct}%</span>
        </div>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[auto_1fr_1fr]">
          {/* Left: Image + main message */}
          <div className="flex flex-col items-center gap-2 md:w-48">
            {img.main_message && (
              <div className="w-full bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-3 mb-1">
                <div className="text-xs font-semibold text-neutral-500 mb-1">{r('mainMessage')}</div>
                <p className="text-sm text-neutral-800 dark:text-neutral-200">{img.main_message}</p>
              </div>
            )}
            {img.url && (
              <img src={img.url} alt={`Pos ${img.position}`} className="w-full max-w-[192px] aspect-square object-cover rounded-lg" loading="lazy" />
            )}
            <span className="px-2 py-0.5 rounded text-xs bg-neutral-200 dark:bg-neutral-600 text-neutral-700 dark:text-neutral-300">{translateImageType(img.image_type)}</span>
          </div>

          {/* Middle: Dimension scores with bars */}
          <div className="space-y-3">
            {dimEntries.map(({ key, val }) => (
              <DimensionRow key={key} label={r(key)} value={val || 0} />
            ))}
          </div>

          {/* Right: What/Why/Fix explanations */}
          <div className="space-y-3">
            {img.what_we_see && (
              <ExplanationBlock num={1} title={r('whatWeSee')} text={img.what_we_see} />
            )}
            {img.why_it_matters && (
              <ExplanationBlock num={2} title={r('whyItMatters')} text={img.why_it_matters} />
            )}
            {img.how_to_fix && (
              <ExplanationBlock num={3} title={r('howToFix')} text={img.how_to_fix} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function DimensionRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="border-b border-neutral-100 dark:border-neutral-700 pb-2 last:border-0 last:pb-0">
      <div className="flex items-center justify-between mb-1">
        <span className={`text-sm font-medium ${value < 40 ? 'text-red-600 dark:text-red-400' : value < 70 ? 'text-neutral-700 dark:text-neutral-300' : 'text-green-700 dark:text-green-400'}`}>{label}</span>
        <span className="text-sm text-neutral-500">{Math.round(value)}</span>
      </div>
      <div className="h-2 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${dimBarColor(value)}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  )
}

function ExplanationBlock({ num, title, text }: { num: number; title: string; text: string }) {
  return (
    <div className="flex gap-2">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-neutral-200 dark:bg-neutral-600 text-2xs font-bold flex items-center justify-center text-neutral-600 dark:text-neutral-300">{num}</span>
      <div>
        <span className="text-sm font-semibold text-neutral-900 dark:text-white">{title}</span>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-0.5">{text}</p>
      </div>
    </div>
  )
}

const IMAGE_TYPE_LABELS: Record<string, string> = {
  main_image: 'Hauptbild', lifestyle: 'Lifestyle', infographic: 'Infografik',
  size_reference: 'Größenvergleich', detail_shot: 'Detailaufnahme',
  what_is_included: 'Lieferumfang', brand_story: 'Markengeschichte',
  comparison: 'Vergleich', packaging: 'Verpackung', in_use: 'In Benutzung',
  feature_highlight: 'Feature-Highlight', variant: 'Variante',
  social_proof: 'Social Proof', before_after: 'Vorher/Nachher',
  close_up: 'Nahaufnahme', context: 'Kontext', dimensions: 'Maße',
  ingredients: 'Inhaltsstoffe', instructions: 'Anleitung', warranty: 'Garantie',
}

function translateImageType(type: string): string {
  return IMAGE_TYPE_LABELS[type] ?? type.replace(/_/g, ' ')
}

/* ---------- Helpers ---------- */

function imageScore(dims: Record<string, number | undefined>): number {
  const vals = [dims.design_quality, dims.perceived_value, dims.message_strength, dims.message_clarity]
  const filled = vals.filter((v): v is number => v != null)
  if (filled.length === 0) return 0
  return Math.round(filled.reduce((a, b) => a + b, 0) / filled.length)
}

function calcAvgDimensions(images: ListingAuditReport['image_audit']['images']) {
  const keys = ['designQuality', 'perceivedValue', 'messageStrength', 'messageClarity'] as const
  const fields = ['design_quality', 'perceived_value', 'message_strength', 'message_clarity'] as const
  return keys.map((key, i) => {
    const vals = images.map((img) => img.dimensions?.[fields[i]]).filter((v): v is number => v != null)
    return { key, avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0 }
  })
}

function dimBarColor(val: number): string {
  if (val >= 70) return 'bg-green-500'
  if (val >= 40) return 'bg-yellow-500'
  return 'bg-red-500'
}

function dimStatusColor(val: number): string {
  if (val >= 70) return 'text-green-600 dark:text-green-400'
  if (val >= 40) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

function dimStatusLabel(val: number, r: (k: string) => string): string {
  if (val >= 70) return r('dimStatusGood')
  if (val >= 40) return r('dimStatusOk')
  return r('dimStatusNeedsWork')
}

function scoreColor(pct: number): string {
  if (pct >= 80) return 'text-green-600 dark:text-green-400'
  if (pct >= 60) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

function scoreBadgeBg(pct: number): string {
  if (pct >= 80) return 'bg-green-500'
  if (pct >= 60) return 'bg-yellow-500'
  return 'bg-red-500'
}

/* ---------- Sub-sections ---------- */

function GalleryAssessment({ ga, r }: { ga: ListingAuditReport['image_audit']['gallery_assessment']; r: (k: string) => string }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="text-sm font-medium mb-2">{r('galleryAssessment')}</h4>
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm">
        <span>{r('diversityScore')}: <strong>{ga.diversity_score}</strong></span>
        {ga.types_missing?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {ga.types_missing.map((type) => (
              <span key={type} className="px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400">{translateImageType(type)}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function VisualNarrative({ vn, r }: { vn: NonNullable<ListingAuditReport['image_audit']['visual_narrative']>; r: (k: string) => string }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="text-sm font-medium mb-2">{r('visualNarrative')}</h4>
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm mb-2">
        <span>{r('coherence')}: <strong>{vn.coherence_score}</strong></span>
        <span>{r('toneConsistency')}: <strong>{vn.tone_consistency}</strong></span>
      </div>
      {vn.story_arc && <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">{r('storyArc')}: {vn.story_arc}</p>}
      {vn.issues?.length > 0 && (
        <ul className="space-y-1 text-xs text-orange-600 dark:text-orange-400">
          {vn.issues.map((issue, i) => <li key={i}>⚠ {issue}</li>)}
        </ul>
      )}
    </div>
  )
}

function MissingTypes({ types, r }: { types: ListingAuditReport['image_audit']['missing_image_types']; r: (k: string) => string }) {
  const sorted = [...types].sort((a, b) => a.priority - b.priority)
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="text-sm font-medium mb-3">{r('missingImageTypes')}</h4>
      <div className="space-y-3">
        {sorted.map((mt, i) => (
          <div key={i} className="flex gap-3 items-start bg-orange-50 dark:bg-orange-900/10 rounded-lg p-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">{mt.priority}</span>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-neutral-900 dark:text-white">{translateImageType(mt.type)}</span>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-0.5">{mt.suggestion}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
